<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import clubAccessService, { type ClubAccess, type ClubAccessPayment, type Pagination } from '../services/club-access.service'
import AccessStatusBadge from '../components/AccessStatusBadge.vue'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t, locale } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const clubName = computed(() => authStore.initData?.club?.name || '')
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user is OWNER or SUPERVISOR
const canViewAccess = computed(() => {
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR'
})

// Data
const clubAccess = ref<ClubAccess | null>(null)
const payments = ref<ClubAccessPayment[]>([])
const loading = ref(false)
const loadingPayments = ref(false)

// Pagination
const currentPage = ref(1)
const perPage = ref(10)
const pagination = ref<Pagination | null>(null)

// Fetch club access
const fetchClubAccess = async () => {
  if (!clubUuid.value || !canViewAccess.value) return

  loading.value = true
  try {
    const response = await clubAccessService.getClubAccess(clubUuid.value)
    clubAccess.value = response.club_access
  } catch (error: any) {
    console.error('Error fetching club access:', error)
    toastStore.error(t('access.error_fetching_access'))
  } finally {
    loading.value = false
  }
}

// Fetch payments
const fetchPayments = async () => {
  if (!clubUuid.value || !canViewAccess.value) return

  loadingPayments.value = true
  try {
    const response = await clubAccessService.getAccessPayments(clubUuid.value, currentPage.value, perPage.value)
    payments.value = response.payments
    pagination.value = response.pagination
  } catch (error: any) {
    console.error('Error fetching payments:', error)
    toastStore.error(t('access.error_fetching_payments'))
  } finally {
    loadingPayments.value = false
  }
}

// Pagination navigation
const goToPage = (page: number) => {
  currentPage.value = page
  fetchPayments()
}

// Open payment page
const openPaymentPage = () => {
  if (!clubAccess.value?.hash) return

  const path = `/public/access-payment/${clubAccess.value.hash}`

  if (Capacitor.isNativePlatform()) {
    router.push(path)
  } else {
    window.open(path, '_blank')
  }
}

// Format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' })
}

// Download invoice
const downloadInvoice = async (payment: ClubAccessPayment) => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const token = localStorage.getItem('auth_token')

    const response = await fetch(`${apiUrl}/v1/club-access-payments/${payment.uuid}/invoice/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/pdf',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: t('access.invoiceDownloadError') }))
      toastStore.error(errorData.message || t('access.invoiceDownloadError'))
      return
    }

    // Download PDF
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `faktura_${payment.uuid}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    toastStore.success(t('access.invoiceDownloaded'))
  } catch (error: any) {
    console.error('Error downloading invoice:', error)
    toastStore.error(t('access.invoiceDownloadError'))
  }
}

onMounted(() => {
  if (canViewAccess.value) {
    fetchClubAccess()
    fetchPayments()
  } else {
    router.push({ name: 'dashboard' })
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <button @click="router.push({ name: 'dashboard' })" class="text-indigo-600 hover:text-indigo-700 flex items-center gap-2 mb-4">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {{ t('common.back') }}
        </button>

        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">{{ t('access.title') }}</h1>
            <p class="text-gray-600 mt-1">{{ clubName }}</p>
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="bg-white rounded-lg shadow-sm p-8 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p class="mt-4 text-gray-600">{{ t('common.loading') }}</p>
      </div>

      <!-- Access info card -->
      <div v-else-if="clubAccess" class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex flex-wrap items-center gap-4 mb-4">
              <h2 class="text-xl font-semibold text-gray-900">{{ t('access.status') }}</h2>
              <AccessStatusBadge :access-to="clubAccess.access_to" :type="clubAccess.type" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <div>
                <p class="text-sm text-gray-600">{{ t('access.type') }}</p>
                <p class="text-lg font-medium text-gray-900">
                  {{ clubAccess.type === 'FREE' ? t('access.trial') : t('access.paid') }}
                </p>
              </div>

              <div>
                <p class="text-sm text-gray-600">{{ t('access.access_to') }}</p>
                <p class="text-lg font-medium text-gray-900 break-words">
                  {{ formatDate(clubAccess.access_to) }}
                </p>
              </div>

              <div>
                <p class="text-sm text-gray-600">{{ t('access.days_remaining') }}</p>
                <p class="text-lg font-medium text-gray-900">
                  {{ clubAccessService.getDaysRemaining(clubAccess.access_to) }}
                </p>
              </div>
            </div>

            <!-- Next payment info -->
            <div v-if="clubAccess.next_amount && clubAccess.next_amount > 0" class="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div class="flex items-start gap-3">
                <svg class="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p class="font-medium text-yellow-900">{{ t('access.payment_required') }}</p>
                  <p class="text-sm text-yellow-800 mt-1">
                    {{ t('access.next_payment_amount') }}: {{ clubAccessService.formatAmount(clubAccess.next_amount * (1 + (clubAccess.vat_rate || 0) / 100), clubAccess.currency) }} {{ t('access.amount_brutto') }}
                  </p>
                  <p class="text-xs text-yellow-700 mt-1">
                    ({{ clubAccessService.formatAmount(clubAccess.next_amount, clubAccess.currency) }} {{ t('access.amount_netto_label') }} + {{ clubAccessService.formatAmount(clubAccess.next_amount * ((clubAccess.vat_rate || 0) / 100), clubAccess.currency) }} {{ t('access.vat_label') }})
                  </p>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="clubAccess.next_amount && clubAccess.next_amount > 0"
            @click="openPaymentPage"
            class="ml-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 hover:shadow-lg transition-all cursor-pointer font-medium"
          >
            {{ t('access.renew') }}
          </button>
        </div>
      </div>

      <!-- Payments history -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">{{ t('access.payment_history') }}</h2>

        <!-- Loading -->
        <div v-if="loadingPayments" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>

        <!-- No payments -->
        <div v-else-if="payments.length === 0" class="text-center py-8 text-gray-500">
          {{ t('access.no_payments') }}
        </div>

        <!-- Payments table -->
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.date') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.amount') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.status') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.provider') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.payer') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('common.invoices') }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="payment in payments" :key="payment.uuid" class="hover:bg-gray-50">
                <td class="px-4 py-4 text-sm text-gray-900">
                  {{ formatDate(payment.created_at) }}
                </td>
                <td class="px-4 py-4 text-sm font-medium text-gray-900">
                  {{ clubAccessService.formatAmount(payment.total_amount, payment.currency) }}
                  <span class="text-xs text-gray-500 block">
                    ({{ clubAccessService.formatAmount(payment.amount, payment.currency) }} + {{ clubAccessService.formatAmount(payment.vat, payment.currency) }} {{ t('access.vat_label') }})
                  </span>
                </td>
                <td class="px-4 py-4 text-sm">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border" :class="clubAccessService.getStatusColorClass(payment.status)">
                    {{ clubAccessService.getStatusText(payment.status) }}
                  </span>
                </td>
                <td class="px-4 py-4 text-sm text-gray-900">
                  {{ payment.provider }}
                </td>
                <td class="px-4 py-4 text-sm text-gray-600">
                  <div>{{ payment.payer_name }}</div>
                  <div class="text-xs">{{ payment.payer_email }}</div>
                </td>
                <td class="px-4 py-4 text-sm">
                  <button
                    v-if="payment.status === 'COMPLETED'"
                    @click="downloadInvoice(payment)"
                    class="inline-flex items-center px-3 py-1.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <svg class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {{ t('access.download') }}
                  </button>
                  <span v-else class="text-gray-400 text-xs">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.last_page > 1" class="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200">
          <!-- Summary text -->
          <p class="text-sm text-gray-600">
            {{ t('access.displaying') }} {{ (pagination.current_page - 1) * pagination.per_page + 1 }} {{ t('access.to') }} {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} {{ t('access.of') }} {{ pagination.total }}
          </p>

          <!-- Page controls -->
          <div class="flex items-center gap-1">
            <!-- Previous -->
            <button
              @click="goToPage(pagination.current_page - 1)"
              :disabled="pagination.current_page === 1"
              class="px-3 py-1.5 text-sm font-medium rounded-md border transition-colors"
              :class="pagination.current_page === 1
                ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-white'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer'"
            >
              {{ t('common.previous') }}
            </button>

            <!-- Page numbers -->
            <template v-for="page in pagination.last_page" :key="page">
              <template v-if="page === 1 || page === pagination.last_page || (page >= pagination.current_page - 2 && page <= pagination.current_page + 2)">
                <button
                  @click="goToPage(page)"
                  class="min-w-[36px] px-2.5 py-1.5 text-sm font-medium rounded-md border transition-colors cursor-pointer"
                  :class="page === pagination.current_page
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'"
                >
                  {{ page }}
                </button>
              </template>
              <template v-else-if="page === pagination.current_page - 3 || page === pagination.current_page + 3">
                <span class="px-1 py-1.5 text-sm text-gray-400">…</span>
              </template>
            </template>

            <!-- Next -->
            <button
              @click="goToPage(pagination.current_page + 1)"
              :disabled="pagination.current_page === pagination.last_page"
              class="px-3 py-1.5 text-sm font-medium rounded-md border transition-colors"
              :class="pagination.current_page === pagination.last_page
                ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-white'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer'"
            >
              {{ t('common.next') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
